// @ts-nocheck
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { OrderModel } from "@insite/client-framework/Types/ApiModels";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import constants from "@store/constants";
import InlineAddress, { InlineAddressPresentationProps } from "@store/Library/Components/InlineAddress";
import LocalizedDateTime from "@store/Overrides/Components/LocalizedDateTime";
import { orderV2Props } from "@store/Themes/common/props/orderV2";
import React, { FC, useContext } from "react";

export interface OrderChangeRequestReviewInformationProps extends WidgetProps {}

export interface OrderChangeRequestReviewInformationStyles {
    boxBottomWrapper?: InjectableCss;
    boxesWrapper?: InjectableCss;
    boxTopWrapper?: InjectableCss;
    fullGridItem?: GridItemProps;
    gridContainer?: GridContainerProps;
    gridItem?: GridItemProps;
    infoLabel?: TypographyPresentationProps;
    infoOldValue?: TypographyPresentationProps;
    infoValue?: TypographyPresentationProps;
    infoWrapper?: InjectableCss;
    inlineAddress?: InlineAddressPresentationProps;
    title?: TypographyPresentationProps;
    wrapper?: InjectableCss;
}

export const OrderChangeRequestReviewInformationStyles: OrderChangeRequestReviewInformationStyles = {
    wrapper: orderV2Props.sectionWrapper,
    title: orderV2Props.sectionTitle,
    gridContainer: orderV2Props.boxesGridContainer,
    gridItem: { width: [12, 6, 6, 6, 6] },
    fullGridItem: { width: 12 },
    boxesWrapper: orderV2Props.boxesWrapper,
    boxTopWrapper: orderV2Props.boxTopWrapper,
    boxBottomWrapper: orderV2Props.boxBottomWrapper,
    infoWrapper: orderV2Props.infoWrapper,
    infoLabel: orderV2Props.infoLabel,
    infoOldValue: orderV2Props.infoOldValue,
    infoValue: orderV2Props.infoValue,
};

const styles = OrderChangeRequestReviewInformationStyles;

const OrderChangeRequestReviewInformation: FC<OrderChangeRequestReviewInformationProps> = () => {
    const { value: order } = useContext(OrderStateContext);
    const { changeRequestOrder } = useAppSelector(state => state.greif.pages.orderChange);
    const {
        newPoNumber,
        newRequestedDate,
        newShipToCode,
        newShipToCompanyName,
        newShipToAddress1,
        newShipToCity,
        newShipToState,
        newShipToPostalCode,
        newShipToCountry,
    } = changeRequestOrder;

    if (!order) {
        return null;
    }

    const newOrderShipTo = {
        stCompanyName: newShipToCompanyName,
        stAddress1: newShipToAddress1,
        shipToCity: newShipToCity,
        shipToState: newShipToState,
        shipToPostalCode: newShipToPostalCode,
        stCountry: newShipToCountry,
    } as OrderModel;

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.title}>{translate("Order Information")}</Typography>
            <GridContainer {...styles.gridContainer}>
                <GridItem {...styles.gridItem}>
                    <StyledWrapper {...styles.boxesWrapper}>
                        <StyledWrapper {...styles.boxTopWrapper}>
                            <StyledWrapper {...styles.infoWrapper}>
                                <Typography {...styles.infoLabel}>{translate("Requested Date")}</Typography>
                                <Typography {...(newRequestedDate ? styles.infoOldValue : styles.infoValue)}>
                                    {order.requestedDeliveryDateDisplay ? (
                                        <LocalizedDateTime dateTime={order.requestedDeliveryDateDisplay} />
                                    ) : (
                                        "—"
                                    )}
                                </Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                        {newRequestedDate && (
                            <StyledWrapper {...styles.boxBottomWrapper}>
                                <StyledWrapper {...styles.infoWrapper}>
                                    <Typography {...styles.infoLabel}>{translate("New Requested Date")}</Typography>
                                    <Typography {...styles.infoValue}>
                                        <LocalizedDateTime dateTime={newRequestedDate} />
                                    </Typography>
                                </StyledWrapper>
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.gridItem}>
                    <StyledWrapper {...styles.boxesWrapper}>
                        <StyledWrapper {...styles.boxTopWrapper}>
                            <StyledWrapper {...styles.infoWrapper}>
                                <Typography {...styles.infoLabel}>{translate("PO Number")}</Typography>
                                <Typography {...(newPoNumber ? styles.infoOldValue : styles.infoValue)}>
                                    {order.customerPO || "—"}
                                </Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                        {newPoNumber && (
                            <StyledWrapper {...styles.boxBottomWrapper}>
                                <StyledWrapper {...styles.infoWrapper}>
                                    <Typography {...styles.infoLabel}>{translate("New PO Number")}</Typography>
                                    <Typography {...styles.infoValue}>{newPoNumber}</Typography>
                                </StyledWrapper>
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.fullGridItem}>
                    <StyledWrapper {...styles.boxesWrapper}>
                        <StyledWrapper {...styles.boxTopWrapper}>
                            <StyledWrapper {...styles.infoWrapper}>
                                <Typography {...styles.infoLabel}>{translate("Shipping Information")}</Typography>
                                <Typography {...(newShipToCode ? styles.infoOldValue : styles.infoValue)}>
                                    <InlineAddress {...styles.inlineAddress} shipTo={order} />
                                </Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                        {newShipToCode && (
                            <StyledWrapper {...styles.boxBottomWrapper}>
                                <StyledWrapper {...styles.infoWrapper}>
                                    <Typography {...styles.infoLabel}>
                                        {translate("New Shipping Information")}
                                    </Typography>
                                    <Typography {...styles.infoValue}>
                                        <InlineAddress {...styles.inlineAddress} shipTo={newOrderShipTo} />
                                    </Typography>
                                </StyledWrapper>
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: OrderChangeRequestReviewInformation,
    definition: {
        group: constants.widgetGroup.orderChangeRequestReview,
        displayName: "Order Change Request Review Information",
        allowedContexts: [constants.pageType.orderChangeRequestReview],
    },
};

export default widgetModule;
